import { inject, Injectable } from "@angular/core";
import { userContextStore } from "../../../../shared/data/user-context.store";

@Injectable({
  providedIn: "root",
})
export class WebStorage {
  private userContext = inject(userContextStore);

  private readonly productFamilyFiltersExcludedKey =
    "class-pia-prod-fam-filter-excludes";
  private readonly productFamilyFavoritesIncludedKey =
    "claas-pia-prod-fam-favs-included";
  private readonly favoriteProductsOnlyKey = "claas-pia-fav-prod-only";
  private readonly primaryDocumentLanguagesKey =
    "claas-pia-primary-document-languages";
  private readonly secondaryDocumentLanguagesKey =
    "claas-pia-secondary-document-languages";
  private readonly productDocumentsFavoritesKey =
    "claas-pia-product-documents-favorites";
  private readonly productArgumentsFavoritesKey =
    "claas-pia-product-arguments-favorites";
  private readonly showDocumentsFavoritesKey =
    "claas-pia-show-documents-favorites";
  private readonly showArgumentsFavoritesKey =
    "claas-pia-show-arguments-favorites";
  private readonly piaDocumentIDKey = "claas-pia-document-id";

  get documentID(): boolean {
    try {
      return JSON.parse(
        (window.localStorage.getItem(this.piaDocumentIDKey) as string) ?? false,
      );
    } catch {
      /* */
    }
    return false;
  }

  set documentID(value: boolean) {
    try {
      window.localStorage.setItem(this.piaDocumentIDKey, JSON.stringify(value));
    } catch {
      // nothing to do
    }
  }

  get uiLanguage(): string {
    // UI language is the language picked in the poff profile menu, provided by
    // the shared UserContextStore (not from localStorage / navigator).
    return this.userContext.language();
  }

  get productFamilyIdsExcluded(): string[] {
    try {
      return (
        JSON.parse(
          window.localStorage.getItem(
            this.productFamilyFiltersExcludedKey,
          ) as string,
        ) ?? []
      );
    } catch {
      /* return empty array no filters excluded */
    }

    return [];
  }

  set productFamilyIdsExcluded(productFamilies: string[]) {
    try {
      window.localStorage.setItem(
        this.productFamilyFiltersExcludedKey,
        JSON.stringify(
          productFamilies && productFamilies?.length ? productFamilies : [],
        ),
      );
    } catch {
      // nothing to do
    }
  }

  get productArgumentsIdsIncluded(): string[] {
    try {
      return (
        JSON.parse(
          window.localStorage.getItem(
            this.productArgumentsFavoritesKey,
          ) as string,
        ) ?? []
      );
    } catch {
      /* return empty array no filters excluded */
    }
    return [];
  }

  set productArgumentsIdsIncluded(productArgumentIds: string[]) {
    try {
      window.localStorage.setItem(
        this.productArgumentsFavoritesKey,
        JSON.stringify(productArgumentIds?.length ? productArgumentIds : []),
      );
    } catch {
      // nothing to do
    }
  }

  get showArgumentsFavorites(): boolean {
    try {
      return JSON.parse(
        (window.localStorage.getItem(
          this.showArgumentsFavoritesKey,
        ) as string) ?? false,
      );
    } catch {
      /* return empty array no filters excluded */
    }

    return false;
  }

  set showArgumentsFavorites(status: boolean) {
    try {
      window.localStorage.setItem(
        this.showArgumentsFavoritesKey,
        JSON.stringify(status),
      );
    } catch {
      // nothing to do
    }
  }

  get productDocumentsIdsIncluded(): string[] {
    try {
      return (
        JSON.parse(
          window.localStorage.getItem(
            this.productDocumentsFavoritesKey,
          ) as string,
        ) ?? []
      );
    } catch {
      /* return empty array no filters excluded */
    }
    return [];
  }

  set productDocumentsIdsIncluded(productDocuments: string[]) {
    try {
      window.localStorage.setItem(
        this.productDocumentsFavoritesKey,
        JSON.stringify(productDocuments?.length ? productDocuments : []),
      );
    } catch {
      // nothing to do
    }
  }

  get showDocumentsFavorites(): boolean {
    try {
      return JSON.parse(
        (window.localStorage.getItem(
          this.showDocumentsFavoritesKey,
        ) as string) ?? false,
      );
    } catch {
      /* return empty array no filters excluded */
    }

    return false;
  }

  set showDocumentsFavorites(status: boolean) {
    try {
      window.localStorage.setItem(
        this.showDocumentsFavoritesKey,
        JSON.stringify(status),
      );
    } catch {
      // nothing to do
    }
  }

  get productFamilyIdsIncluded(): string[] {
    try {
      return (
        JSON.parse(
          window.localStorage.getItem(
            this.productFamilyFavoritesIncludedKey,
          ) as string,
        ) ?? []
      );
    } catch {
      /* return empty array no filters excluded */
    }
    return [];
  }

  set productFamilyIdsIncluded(productFamilies: string[]) {
    try {
      window.localStorage.setItem(
        this.productFamilyFavoritesIncludedKey,
        JSON.stringify(
          productFamilies && productFamilies?.length ? productFamilies : [],
        ),
      );
    } catch {
      // nothing to do
    }
  }

  get favoriteProductsOnly(): boolean {
    try {
      return JSON.parse(
        (window.localStorage.getItem(this.favoriteProductsOnlyKey) as string) ??
          false,
      );
    } catch {
      return false;
    }
  }

  set favoriteProductsOnly(status: boolean) {
    try {
      window.localStorage.setItem(
        this.favoriteProductsOnlyKey,
        JSON.stringify(status),
      );
    } catch {
      // nothing to do
    }
  }

  get primaryDocumentLanguagesStored(): Record<string, string[]> | null {
    try {
      return (
        JSON.parse(
          window.localStorage.getItem(
            this.primaryDocumentLanguagesKey,
          ) as string,
        ) ?? null
      );
    } catch {
      /* return null */
    }

    return null;
  }

  set primaryDocumentLanguagesStored(
    documentLanguages: Record<string, string[]>,
  ) {
    try {
      window.localStorage.setItem(
        this.primaryDocumentLanguagesKey,
        JSON.stringify(documentLanguages),
      );
    } catch {
      // nothing to do
    }
  }

  get secondaryDocumentLanguagesStored(): Record<string, string[]> | null {
    try {
      return (
        JSON.parse(
          window.localStorage.getItem(
            this.secondaryDocumentLanguagesKey,
          ) as string,
        ) ?? null
      );
    } catch {
      /* return null */
    }

    return null;
  }

  set secondaryDocumentLanguagesStored(
    documentLanguages: Record<string, string[]>,
  ) {
    try {
      window.localStorage.setItem(
        this.secondaryDocumentLanguagesKey,
        JSON.stringify(documentLanguages),
      );
    } catch {
      // nothing to do
    }
  }
}
